import React, { useState, useEffect } from 'react';
import { Search, MapPin, Calendar, Eye, X, ChevronDown } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useEventImage } from '../hooks/useEventThumbnail';
import { goToEventDetail, goToLogin } from '../utils/navigation';
import Login from './Login';


const ExploreEvents: React.FC = () => {
  const { user, token } = useAuth();
  
  // State for events and loading
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states - same as home page
  // Initialize city and search directly from URL to avoid double fetch on mount
  const initialCityParam = (() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('city');
  })();
  const [searchTerm, setSearchTerm] = useState(initialCityParam || '');
  const [selectedCity, setSelectedCity] = useState<string | null>(initialCityParam);
  
  // Modal states
  const [showLogin, setShowLogin] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Fetch events when a city is selected (public endpoint). This avoids calling "all" on init when city exists.
  useEffect(() => {
    const fetchCityEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {
          page: currentPage,
          limit: itemsPerPage,
          search: selectedCity || undefined,
        };
        const response = await apiService.getAllEvents(token as string, params);

        let eventsData: any[] = [];
        let paginationData: any = null;
        if (response && typeof response === 'object' && 'events' in response && Array.isArray((response as any).events)) {
          eventsData = (response as any).events;
          paginationData = (response as any).pagination;
        } else if (Array.isArray(response)) {
          eventsData = response;
        } else if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as any).data)) {
          eventsData = (response as any).data;
        } else if (response && typeof response === 'object' && 'data' in response && (response as any).data && typeof (response as any).data === 'object' && 'events' in (response as any).data && Array.isArray((response as any).data.events)) {
          eventsData = (response as any).data.events;
          paginationData = (response as any).data.pagination;
        }

        setEvents(eventsData);
        setTotalPages(paginationData?.totalPages || paginationData?.pages || 1);
        setTotalItems(paginationData?.totalItems || paginationData?.total || eventsData.length);
      } catch (err) {
        console.error('Failed to fetch city events:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch events');
      } finally {
        setLoading(false);
      }
    };

    if (selectedCity) {
      fetchCityEvents();
    }
  }, [selectedCity, currentPage, itemsPerPage]);

  // Fetch all events when no city is selected and user is authenticated
  useEffect(() => {
    const fetchAllEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm || undefined,
        };
        const response = await apiService.getAllEvents(token as string, params);

        let eventsData: any[] = [];
        let paginationData: any = null;
        if (response && typeof response === 'object' && 'events' in response && Array.isArray((response as any).events)) {
          eventsData = (response as any).events;
          paginationData = (response as any).pagination;
        } else if (Array.isArray(response)) {
          eventsData = response;
        } else if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as any).data)) {
          eventsData = (response as any).data;
        } else if (response && typeof response === 'object' && 'data' in response && (response as any).data && typeof (response as any).data === 'object' && 'events' in (response as any).data && Array.isArray((response as any).data.events)) {
          eventsData = (response as any).data.events;
          paginationData = (response as any).data.pagination;
        }

        setEvents(eventsData);
        setTotalPages(paginationData?.totalPages || paginationData?.pages || 1);
        setTotalItems(paginationData?.totalItems || paginationData?.total || eventsData.length);
      } catch (err) {
        console.error('Failed to fetch all events:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch events');
      } finally {
        setLoading(false);
      }
    };

    if (!selectedCity) {
      if (user && token) {
        fetchAllEvents();
      } else if (!user) {
        setShowLogin(true);
        setLoading(false);
      }
    }
  }, [user, token, currentPage, itemsPerPage, searchTerm, selectedCity]);

  // Handle city selection
  const handleCitySelect = (city: string | null) => {
    setSelectedCity(city);
    // Update search term when city is selected
    if (city) {
      setSearchTerm(city);
    } else {
      setSearchTerm('');
    }
  };



  // Clear all filters
  const clearAllFilters = () => {
    setSelectedCity(null);
    setSearchTerm('');
    setCurrentPage(0);
  };

  // If user is not logged in, show login prompt
  if (!user) {
    return (
      <main className="pt-32 pb-16 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="mb-8">
                <h1 className="font-runtime text-3xl font-bold text-black mb-4">Login Required</h1>
                <p className="font-lucy-rose text-gray-600 mb-6">
                  Please log in to explore all events and access detailed information.
                </p>
              </div>
              
              <button
                onClick={() => setShowLogin(true)}
                className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl mb-4"
              >
                Login to Continue
              </button>
              
              <p className="text-sm text-gray-500">
                Don't have an account? <button onClick={() => setShowLogin(true)} className="text-dpn-pink hover:text-dpn-raspberry underline">Sign up here</button>
              </p>
            </div>
          </div>
        </div>
        
        {/* Login Modal */}
        {showLogin && (
          <Login 
            onClose={() => setShowLogin(false)} 
            onSwitchToSignup={() => setShowLogin(false)}
          />
        )}
      </main>
    );
  }

  // Component for rendering event image with thumbnail API support
  const EventImage: React.FC<{ event: any }> = ({ event }) => {
    const eventId = event.id || event.EVENT_ID;
    const { thumbnailUrl, loading } = useEventImage(event, eventId);
    
    if (loading) {
      return (
        <div className="w-full h-72 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      );
    }
    
    return (
      <img 
        src={thumbnailUrl || 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=400'}
        alt={event.TITLE || event.title || 'Event'} 
        className="w-full h-full object-contain"
        onError={(e) => {
          console.error('❌ Image failed to load for event:', event.title || event.TITLE);
          e.currentTarget.src = 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=400';
        }}
      />
    );
  };



  return (
    <main className="pt-2 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-runtime text-3xl md:text-4xl font-bold text-black">Explore Events</h1>
          <p className="font-lucy-rose text-gray-600 mt-2">Browse all events and refine with advanced filters.</p>
          

        </div>

        {/* Search Bar */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 h-6 w-6" />
            <input
              type="text"
              placeholder="Search by city, country, or puja name..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                // Clear location filter when user starts typing in search
                if (value.trim() !== '' && selectedCity) {
                  setSelectedCity(null);
                }
              }}
              className="w-full pl-14 pr-6 py-4 border border-gray-200 rounded-full bg-white focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose text-base sm:text-lg shadow-sm"
            />
          </div>
        </div>

        {/* Main Filter Chips */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex justify-center gap-4">
            {/* Location Filter - Static display showing current selection */}
            <div className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full shadow-sm">
              <MapPin className="h-5 w-5 text-dpn-light-green" />
              <span className="font-lucy-rose font-medium text-gray-800">
                {selectedCity ? selectedCity : 'Location'}
              </span>
            </div>



            {/* More Filters - Disabled */}
            <button
              disabled
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 border border-gray-200 rounded-full shadow-sm cursor-not-allowed opacity-60"
              title="Coming Soon"
            >
              <ChevronDown className="h-5 w-5 text-gray-400" />
              <span className="font-lucy-rose font-medium text-gray-500">More Filters</span>
              <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded-full">Coming Soon</span>
            </button>

          </div>
        </div>

        {/* Clear Filters Section */}
        {(selectedCity || searchTerm) && (
          <div className="max-w-4xl mx-auto mb-6 text-center">
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-50 border-2 border-red-200 rounded-full hover:bg-red-100 transition-all duration-200 font-lucy-rose font-medium text-red-600 hover:text-red-700 hover:border-red-300"
            >
              <X className="h-5 w-5" />
              Clear All Filters
            </button>
          </div>
        )}

        {/* City Filters */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex flex-wrap justify-center gap-3">
            {['Kolkata', 'Darjeeling', 'Siliguri', 'Durgapur', 'Asansol', 'Howrah'].map((city) => (
              <button
                key={city}
                onClick={() => handleCitySelect(selectedCity === city ? null : city)}
                className={`px-4 py-2 rounded-full text-sm font-lucy-rose transition-colors ${
                  selectedCity === city
                    ? 'bg-dpn-pink text-white shadow-md'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {city}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink"></div>
              <span className="font-lucy-rose text-gray-600">Loading events...</span>
            </div>
          </div>
        ) : error ? (
          <div className="text-center text-red-600 py-16">Error loading events: {error}</div>
        ) : (
          <div>
            <p className="font-lucy-rose text-gray-600 mb-4">Showing {events.length} of {totalItems} events</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {events.map((event: any) => (
                <div key={event.EVENT_ID || event.id} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden flex flex-col hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="h-72 overflow-hidden">
                    <EventImage event={event} />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-runtime text-lg font-semibold text-gray-900 mb-1">
                      {event.TITLE || event.title || 'Untitled Event'}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{event.LOCATION || event.location || event.ADDRESS || event.address || 'Location TBA'}</span>
                    </div>
                    {event.START_DATE && (
                      <div className="flex items-center text-sm text-gray-600 mb-4">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{new Date(event.START_DATE).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="mt-auto">
                      <button
                        onClick={() => {
                          // Check if user is logged in
                          if (!user) {
                            // User is not logged in, redirect to login page
                            goToLogin();
                            return;
                          }
                          // User is logged in, proceed to event details
                          goToEventDetail(event.id, event.title, event.location);
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 bg-dpn-pink hover:bg-dpn-raspberry text-white px-4 py-2 rounded-lg transition-colors font-lucy-rose"
                      >
                        <Eye className="h-4 w-4" /> View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
          {/* Page Size Selector */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(0); // Reset to first page when changing size
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent text-sm"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <span className="text-sm text-gray-600">
              of {totalItems} events
            </span>
          </div>

          {/* Simple Page Navigation */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {/* Page Info */}
              <div className="text-sm text-gray-600">
                Page {currentPage + 1} of {totalPages} • {totalItems} total events
              </div>

              {/* Simple Page Buttons */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => {
                    setCurrentPage(prev => Math.max(0, prev - 1));
                  }}
                  disabled={currentPage === 0}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm font-medium"
                >
                  ← Previous
                </button>

                {/* Page Numbers - Simple 1, 2, 3... */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setCurrentPage(i);
                      }}
                      className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                        i === currentPage
                          ? 'bg-dpn-pink text-white border-dpn-pink'
                          : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  
                  {/* Show ellipsis if more than 10 pages */}
                  {totalPages > 10 && (
                    <>
                      <span className="px-2 text-gray-400">...</span>
                      <button
                        onClick={() => setCurrentPage(totalPages - 1)}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => {
                    setCurrentPage(prev => prev + 1);
                  }}
                  disabled={currentPage >= totalPages - 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm font-medium"
                >
                  Next →
                </button>
              </div>

              {/* Quick Jump to Page */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Go to page:</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage + 1}
                  onChange={(e) => {
                    const page = parseInt(e.target.value) - 1;
                    if (page >= 0 && page < totalPages) {
                      setCurrentPage(page);
                    }
                  }}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:ring-2 focus:ring-dpn-pink focus:border-transparent"
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('input[type="number"]') as HTMLInputElement;
                    if (input) {
                      const page = parseInt(input.value) - 1;
                      if (page >= 0 && page < totalPages) {
                        setCurrentPage(page);
                      }
                    }
                  }}
                  className="px-3 py-1 bg-dpn-pink text-white rounded text-sm hover:bg-dpn-raspberry transition-colors"
                >
                  Go
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter Modals */}
      





    </main>
  );
};

export default ExploreEvents;
